import React, { useState, useEffect, useReducer, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import MobileActionMenu from "../../components/MobileActionMenu";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ReceiptIcon from "@material-ui/icons/Receipt";
import LinkIcon from "@material-ui/icons/Link";
import VisibilityIcon from "@material-ui/icons/Visibility";

import ConfirmationModal from "../../components/ConfirmationModal";
import FaturaModal from "../../components/FaturaModal";
import PagamentoModal from "../../components/PagamentoModal";
import { listFinanceiroFaturas, deleteFinanceiroFatura } from "../../services/financeiroFaturas";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import moment from "moment";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_FATURAS": {
      const incoming = action.payload || [];
      const updated = [...state];
      incoming.forEach(fatura => {
        const index = updated.findIndex(f => f.id === fatura.id);
        if (index !== -1) updated[index] = fatura;
        else updated.push(fatura);
      });
      return updated;
    }
    case "UPDATE_FATURA": {
      const updated = state.map(f => (f.id === action.payload.id ? action.payload : f));
      return updated;
    }
    case "DELETE_FATURA":
      return state.filter(f => f.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: "transparent",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: "1px solid #e0e0e0",
    flexWrap: "wrap",
    gap: 12,
    [theme.breakpoints.down("sm")]: { padding: "12px 16px", flexDirection: "column", alignItems: "stretch" },
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  headerTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#1a1a1a",
    [theme.breakpoints.down("sm")]: { fontSize: "1.2rem" },
  },
  headerSubtitle: {
    fontSize: "0.875rem",
    color: "#666",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: { flexDirection: "column", alignItems: "stretch" },
  },
  searchField: {
    backgroundColor: "#fff",
    borderRadius: 8,
    minWidth: 220,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#1976d2" },
    },
    [theme.breakpoints.down("sm")]: { minWidth: "unset", width: "100%" },
  },
  filterSelect: {
    minWidth: 150,
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-root": { borderRadius: 8 },
    [theme.breakpoints.down("sm")]: { minWidth: "unset", width: "100%" },
  },
  addButton: {
    borderRadius: 8,
    padding: "6px 20px",
    textTransform: "none",
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: { width: "100%" },
  },
  content: {
    padding: "16px 24px 24px",
    [theme.breakpoints.down("sm")]: { padding: "12px 12px 16px" },
  },
  tableWrapper: {
    backgroundColor: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  tableScroll: { overflowX: "auto", width: "100%" },
  tableHead: {
    backgroundColor: "var(--primary-color, #1e293b)",
    "& th": {
      color: "#fff",
      fontWeight: 600,
      fontSize: "0.8rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      borderBottom: "none",
      padding: "14px 16px",
    },
  },
  tableBody: {
    "& td": {
      padding: "12px 16px",
      fontSize: "0.875rem",
      color: "#334155",
      borderBottom: "1px solid #f1f5f9",
    },
    "& tr:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderTop: "1px solid #f1f5f9",
    backgroundColor: "#fff",
    borderRadius: "0 0 12px 12px",
  },
  actionBtn: {
    minWidth: "auto",
    padding: "4px 8px",
    borderRadius: 6,
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "none",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    color: "#999",
  },
}));

const STATUS_OPTIONS = [
  { value: "all", label: "Todos os status" },
  { value: "aberta", label: "Aberta" },
  { value: "paga", label: "Paga" },
  { value: "vencida", label: "Vencida" },
  { value: "cancelada", label: "Cancelada" },
];

const getStatusStyle = (status) => {
  switch (status) {
    case "aberta":    return { text: "Aberta",    color: "#f59e0b", backgroundColor: "#fef3c7" };
    case "paga":      return { text: "Paga",      color: "#10b981", backgroundColor: "#d1fae5" };
    case "vencida":   return { text: "Vencida",   color: "#ef4444", backgroundColor: "#fee2e2" };
    case "cancelada": return { text: "Cancelada", color: "#6b7280", backgroundColor: "#f3f4f6" };
    default:          return { text: "—",         color: "#6b7280", backgroundColor: "#f3f4f6" };
  }
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);

const Faturas = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const primaryColor = theme.palette.primary.main || "#3b82f6";
  const { user } = useContext(AuthContext);
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [apiPage, setApiPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [faturas, dispatch] = useReducer(reducer, []);

  const [tablePage, setTablePage] = useState(0);
  const rowsPerPage = 10;

  const [faturaModalOpen, setFaturaModalOpen] = useState(false);
  const [editingFatura, setEditingFatura] = useState({});
  const [pagamentoModalOpen, setPagamentoModalOpen] = useState(false);
  const [selectedFatura, setSelectedFatura] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingFatura, setDeletingFatura] = useState(null);

  // Reinicia quando muda busca ou filtro
  useEffect(() => {
    dispatch({ type: "RESET" });
    setApiPage(1);
    setTablePage(0);
  }, [searchParam, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => { fetchFaturas(apiPage); }, apiPage === 1 ? 300 : 0);
    return () => clearTimeout(timer);
  }, [searchParam, statusFilter, apiPage]);

  const fetchFaturas = async (page) => {
    setLoading(true);
    try {
      const response = await listFinanceiroFaturas({
        searchParam,
        pageNumber: page,
        status: statusFilter === "all" ? "" : statusFilter,
        companyId: user.companyId,
      });

      const rows = response.faturamentos || response.faturas || response.records || (Array.isArray(response) ? response : []);
      dispatch({ type: "LOAD_FATURAS", payload: rows });
      setHasMore(response.hasMore || false);
      setTotalCount(response.count || 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  // Ao avançar de página, carrega próximo batch da API se necessário
  const handleNextPage = () => {
    const nextPage = tablePage + 1;
    const neededItems = (nextPage + 1) * rowsPerPage;
    if (neededItems > faturas.length && hasMore) {
      setApiPage(prev => prev + 1);
    }
    setTablePage(nextPage);
  };

  const handlePrevPage = () => {
    setTablePage(prev => Math.max(0, prev - 1));
  };

  const handlePageClick = (idx) => {
    const neededItems = (idx + 1) * rowsPerPage;
    if (neededItems > faturas.length && hasMore) {
      setApiPage(prev => prev + 1);
    }
    setTablePage(idx);
  };

  const paginatedItems = faturas.slice(tablePage * rowsPerPage, (tablePage + 1) * rowsPerPage);
  // Total visual pages: usa totalCount do servidor para saber quantas páginas existem
  const totalVisualPages = Math.max(
    Math.ceil(faturas.length / rowsPerPage),
    totalCount ? Math.ceil(totalCount / rowsPerPage) : 0
  );

  const handleOpenNew = () => { setEditingFatura({}); setFaturaModalOpen(true); };
  const handleEdit = (f) => { setEditingFatura(f); setFaturaModalOpen(true); };
  const handleDelete = (f) => { setDeletingFatura(f); setConfirmModalOpen(true); };

  const handleConfirmDelete = async () => {
    if (!deletingFatura) return;
    try {
      await deleteFinanceiroFatura(deletingFatura.id);
      dispatch({ type: "DELETE_FATURA", payload: deletingFatura.id });
      setTotalCount(c => c - 1);
      toast.success("Fatura excluída com sucesso!");
      setConfirmModalOpen(false);
      setDeletingFatura(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCopyLink = (f) => {
    const link = (f.status !== "paga" && f.paymentLink)
      ? f.paymentLink
      : `${window.location.origin}/faturas/${f.id}`;
    navigator.clipboard.writeText(link);
    toast.success((f.status !== "paga" && f.paymentLink) ? "Link de pagamento copiado!" : "Link da fatura copiado!");
  };

  return (
    <Box className={classes.root} style={{ "--primary-color": primaryColor }}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Box>
            <Typography className={classes.headerTitle}>Faturas</Typography>
            <Typography className={classes.headerSubtitle}>
              {totalCount > 0 ? `${totalCount} fatura(s) encontrada(s)` : "Gerencie as faturas de clientes"}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.headerRight}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Pesquisar..."
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" variant="outlined" className={classes.filterSelect}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              {STATUS_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenNew}
            className={classes.addButton}
          >
            Nova Fatura
          </Button>
        </Box>
      </Box>

      {/* Conteúdo */}
      <Box className={classes.content}>
        <Box className={classes.tableWrapper}>
          <Box className={classes.tableScroll}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell align="center">Vencimento</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {loading && faturas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box className={classes.emptyState}>
                      <CircularProgress size={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : paginatedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box className={classes.emptyState}>
                      <ReceiptIcon style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }} />
                      <Typography>Nenhuma fatura encontrada</Typography>
                      <Typography variant="body2" style={{ marginTop: 4 }}>
                        {searchParam || statusFilter !== "all"
                          ? "Tente ajustar os filtros de busca"
                          : "Crie sua primeira fatura clicando em \"Nova Fatura\""}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedItems.map((fatura) => {
                  const st = getStatusStyle(fatura.status);
                  return (
                    <TableRow key={fatura.id}>
                      <TableCell style={{ color: "#94a3b8", fontWeight: 500 }}>
                        {fatura.id}
                      </TableCell>
                      <TableCell style={{ fontWeight: 500 }}>
                        {fatura.descricao || `FAT-${String(fatura.id).padStart(4, "0")}`}
                      </TableCell>
                      <TableCell>
                        <Typography style={{ fontWeight: 500, fontSize: "0.875rem" }}>
                          {fatura.client?.name || "—"}
                        </Typography>
                        {fatura.client?.email && (
                          <Typography variant="caption" style={{ color: "#94a3b8" }}>
                            {fatura.client.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" style={{ fontWeight: 700 }}>
                        {formatCurrency(fatura.valor)}
                      </TableCell>
                      <TableCell align="center">
                        {fatura.dataVencimento && moment(fatura.dataVencimento).isValid()
                          ? moment(fatura.dataVencimento).format("DD/MM/YYYY")
                          : "—"}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={st.text}
                          size="small"
                          style={{ backgroundColor: st.backgroundColor, color: st.color, fontWeight: 600, fontSize: "0.75rem" }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {isMobile ? (
                          <MobileActionMenu actions={[
                            { label: "Ver detalhes", icon: <VisibilityIcon fontSize="small" />, onClick: () => history.push(`/faturas/${fatura.id}`) },
                            { label: "Editar", icon: <EditIcon fontSize="small" />, onClick: () => handleEdit(fatura) },
                            { label: "Copiar link", icon: <LinkIcon fontSize="small" />, onClick: () => handleCopyLink(fatura) },
                            { label: "Registrar pagamento", icon: <ReceiptIcon fontSize="small" />, onClick: () => { setSelectedFatura(fatura); setPagamentoModalOpen(true); }, hidden: fatura.status !== "aberta" },
                            { label: "Excluir", icon: <DeleteIcon fontSize="small" />, onClick: () => handleDelete(fatura), color: "#ef4444" },
                          ]} />
                        ) : (
                          <Box display="flex" justifyContent="center" style={{ gap: 4 }}>
                            <Tooltip title="Ver detalhes">
                              <IconButton size="small" style={{ color: "#6366f1" }} onClick={() => history.push(`/faturas/${fatura.id}`)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton size="small" style={{ color: "#1976d2" }} onClick={() => handleEdit(fatura)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copiar link">
                              <IconButton size="small" style={{ color: "#0891b2" }} onClick={() => handleCopyLink(fatura)}>
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {fatura.status === "aberta" && (
                              <Tooltip title="Registrar pagamento">
                                <IconButton size="small" style={{ color: "#10b981" }} onClick={() => { setSelectedFatura(fatura); setPagamentoModalOpen(true); }}>
                                  <ReceiptIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Excluir">
                              <IconButton size="small" style={{ color: "#ef4444" }} onClick={() => handleDelete(fatura)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          </Box>

          {/* Barra de paginação */}
          {(faturas.length > 0 || totalCount > 0) && (
            <Box className={classes.paginationBar}>
              <Typography variant="body2" style={{ color: "#666" }}>
                {faturas.length > 0
                  ? `Exibindo ${tablePage * rowsPerPage + 1} a ${Math.min((tablePage + 1) * rowsPerPage, faturas.length)} de ${totalCount || faturas.length} resultado(s)`
                  : "Carregando..."}
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Button
                  size="small"
                  disabled={tablePage === 0 || loading}
                  onClick={handlePrevPage}
                  className={classes.actionBtn}
                >
                  Anterior
                </Button>
                {Array.from({ length: Math.min(totalVisualPages, 5) }, (_, i) => {
                  let pageIdx = i;
                  if (totalVisualPages > 5) {
                    const start = Math.max(0, Math.min(tablePage - 2, totalVisualPages - 5));
                    pageIdx = start + i;
                  }
                  return (
                    <Button
                      key={pageIdx}
                      size="small"
                      variant={pageIdx === tablePage ? "contained" : "text"}
                      color={pageIdx === tablePage ? "primary" : "default"}
                      onClick={() => handlePageClick(pageIdx)}
                      style={{ minWidth: 32, borderRadius: 6, fontWeight: pageIdx === tablePage ? 700 : 400 }}
                    >
                      {pageIdx + 1}
                    </Button>
                  );
                })}
                <Button
                  size="small"
                  disabled={(tablePage >= totalVisualPages - 1 && !hasMore) || loading}
                  onClick={handleNextPage}
                  className={classes.actionBtn}
                >
                  {loading ? <CircularProgress size={14} /> : "Próximo"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Modais */}
      <FaturaModal
        open={faturaModalOpen}
        onClose={() => setFaturaModalOpen(false)}
        fatura={editingFatura}
        onSaved={(saved) => {
          dispatch({ type: "LOAD_FATURAS", payload: [saved] });
          if (!editingFatura?.id) setTotalCount(c => c + 1);
          setFaturaModalOpen(false);
        }}
      />

      <PagamentoModal
        open={pagamentoModalOpen}
        onClose={() => setPagamentoModalOpen(false)}
        fatura={selectedFatura}
        onSaved={(updated) => {
          if (updated) dispatch({ type: "UPDATE_FATURA", payload: updated });
          setPagamentoModalOpen(false);
        }}
      />

      <ConfirmationModal
        title="Excluir Fatura"
        open={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false); setDeletingFatura(null); }}
        onConfirm={handleConfirmDelete}
      >
        Tem certeza que deseja excluir esta fatura? Esta ação não pode ser desfeita.
      </ConfirmationModal>
    </Box>
  );
};

export default Faturas;
